import { html, rune, View } from 'rune-ts';
import klass from './LandingInputFile.module.scss';
import { fileUpload, ImageCheckOptions } from '../../../../shared/client/file';
import { loading, loadingEnd } from '../../../../shared/client/global';
import { ConfigSentryF } from '../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { Files } from '../../../../entities/pool/public/files';
import { typoPcMo } from '../../../../shared/typography/typo';
import { PlusIcon, TrashBinIcon } from '../../atoms/Icon';
import { MShopUtilF } from '../../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { htmlIf } from '../../../../shared/util';
import { Alert } from '../Alert/Alert';
import { preventEscapeErrorJson } from '../../../../shared/util/preventEscapeErrorJson';

export type LandingInputFileProps = {
  name: string;
  check_option?: ImageCheckOptions;
  placeholder?: string;
  helper_message?: string;
};

export class LandingInputFile extends View<LandingInputFileProps> {
  file?: Files;
  is_error = false;

  protected override template() {
    const { name, placeholder = '', helper_message = '' } = this.data;
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <div
        class="${klass.input_container}"
        data-error="${this.is_error}"
        data-upload="${this.file ? 'active' : 'inActive'}"
      >
        <div class="${klass.input_wrapper}">
          <div class="${klass.preview}">
            <a
              class="${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}"
              href="${this.file?.url ?? '#'}"
              target="_blank"
              download="${this.file?.original_name}"
              >${this.file?.original_name}
            </a>
            <div class="${klass.minus_icon}">${TrashBinIcon}</div>
          </div>

          <div class="${klass.placeholder} ${typoPcMo({ is_mobile, pc: '16_medium', mo: '14_medium' })}">
            ${placeholder}
          </div>
          <label class="${klass.input_label}" for="${name}_file">
            <input
              name="${name}"
              id="${name}_file"
              type="file"
              accept="image/jpg, image/jpeg, image/png, .ppt, .pptx, .keynote, .pdf, application/pdf, .xlsx, .key"
              class="${klass.input}"
              autofocus
            />

            <div class="${klass.plus_icon}">${PlusIcon}</div>
          </label>
        </div>
        ${htmlIf(
          html`
            <div class="${klass.helper_message} ${typoPcMo({ is_mobile, pc: '12_medium', mo: '12_medium' })}">
              ${helper_message}
            </div>
          `,
          !!helper_message,
        )}
      </div>
    `;
  }

  private setError(value: boolean) {
    this.element().dataset.error = `${value}`;
    this.is_error = value;
  }

  protected override onRender() {
    this.delegate('change', 'input[type="file"]', async (e) => {
      const input_el = e.currentTarget as HTMLInputElement;

      const file = input_el.files?.[0];

      if (!file) return;

      try {
        loading();
        const resFile = await fileUpload(input_el, {
          url: '/@api/mp_shop/landing/file_upload',
        });
        loadingEnd();
        this.file = resFile;
        this.setError(false);

        this.redraw();
      } catch (e) {
        loadingEnd();
        this.setError(true);

        if (e instanceof XMLHttpRequest) {
          const error_data = JSON.parse(e.responseText);

          if (error_data.message === 'Not accepted File Types') {
            return MShopUtilF.popToastMsg(ET('mps2::landing::file::not_available_file'), 'info');
          }
          if (error_data.message === 'File too large') {
            return MShopUtilF.popToastMsg(ET('mps2::landing::file::file_size_error'), 'info');
          }
        }

        ConfigSentryF.error(e);
        return Alert.open(
          preventEscapeErrorJson({
            title: ET('mps2::common::error_title'),
            message: ET('mps2::common::error_message'),
          }),
        );
      }
    });
    this.delegate('click', `.${klass.minus_icon}`, async () => {
      this.file = undefined;
      this.redraw();
    });
  }
}
